import {useParams} from '@github-ui/react-router'
import {RepoIssuesPageLayout} from '@github-ui/repo-issues-page-layout/RepoIssuesPageLayout'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import type {UserShortcut} from '@github-ui/saved-views'
import {useNavigate} from '@github-ui/use-navigate'
import {useCallback, useEffect} from 'react'

import {renderIssuesSidebar} from '../../components/sidebar/render-issues-sidebar'
import {ViewsBackLink} from '../../components/views/ViewsBackLink'
import {LABELS} from '../../constants/labels'
import {useConfirmDiscardChanges} from '../../hooks/use-confirm-discard-changes'
import {useSavedViewEditor} from '../../hooks/use-saved-view-editor'
import {IssueViewsPage, RepositorySavedViewPage} from '../../lazy-routes'
import {preloadWhenIdle} from '../../utils/preload-when-idle'
import {AnalyticsWrapper} from '../AnalyticsWrapper'
import {RepositoryViewNewContent} from './RepositoryViewNewContent'

const INITIAL_SHORTCUT: UserShortcut = {
  id: '',
  name: '',
  searchType: 'issues',
  query: 'is:issue',
  description: '',
  icon: 'bookmark',
  color: 'gray',
}

export function RepositoryViewNewPage() {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const navigate = useNavigate()
  const editor = useSavedViewEditor({initialShortcut: INITIAL_SHORTCUT})
  const {isDirty} = editor
  const {confirmDiscardIfDirty} = useConfirmDiscardChanges({isDirty})

  useEffect(() => {
    return preloadWhenIdle(() => {
      IssueViewsPage.preload()
      RepositorySavedViewPage.preload()
    })
  }, [])

  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  const viewsPath = `/${owner}/${repo}/issues/views`

  const handleBackClick = useCallback(
    async (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (e.defaultPrevented || e.button !== 0) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      if (!isDirty) return

      e.preventDefault()
      if (await confirmDiscardIfDirty()) {
        navigate(viewsPath)
      }
    },
    [isDirty, confirmDiscardIfDirty, navigate, viewsPath],
  )

  const contextArea = sidebarLayoutEnabled ? (
    <ViewsBackLink viewsPath={viewsPath} onClick={handleBackClick} />
  ) : undefined

  return (
    <AnalyticsWrapper category="Repository View New">
      <RepoIssuesPageLayout
        dialogTitle={LABELS.viewsTitle}
        title={sidebarLayoutEnabled ? LABELS.views.createLink : undefined}
        contextArea={contextArea}
        renderSidebar={renderIssuesSidebar}
      >
        <RepositoryViewNewContent
          editor={editor}
          confirmDiscardIfDirty={confirmDiscardIfDirty}
          onBackLinkClick={handleBackClick}
        />
      </RepoIssuesPageLayout>
    </AnalyticsWrapper>
  )
}
